import type { SimNPC } from '../types/SimNPC';
import type { CardWithId } from '../../types/card';

/**
 * デッキ比較結果
 */
export interface DeckComparisonResult {
  similarity: number; // 類似度 (0-100%)
  commonCards: CardCountMap; // 共通カード
  deck1OnlyCards: CardCountMap; // デッキ1のみのカード
  deck2OnlyCards: CardCountMap; // デッキ2のみのカード
  attributeComparison: AttributeComparison;
  costComparison: CostComparison;
}

/**
 * カードID → 枚数のマップ
 */
export interface CardCountMap {
  [cardId: string]: {
    count: number;
    name: string;
    attribute: string;
    cost: number;
  };
}

/**
 * 属性構成の比較
 */
export interface AttributeComparison {
  deck1: { [attribute: string]: number };
  deck2: { [attribute: string]: number };
}

/**
 * コスト分布の比較
 */
export interface CostComparison {
  deck1: { low: number; mid: number; high: number }; // 0-2, 3-5, 6+
  deck2: { low: number; mid: number; high: number };
}

/**
 * 世代別デッキ統計
 */
export interface GenerationDeckStats {
  generation: number;
  npcCount: number;
  avgRating: number;
  avgWinRate: number;
  topCards: Array<{ cardId: string; name: string; avgCount: number; usageRate: number }>;
  attributeDistribution: { [attribute: string]: number };
}

/**
 * デッキをカードID → 枚数のマップに変換
 */
function deckToCardCountMap(deck: CardWithId[]): Map<string, { count: number; card: CardWithId }> {
  const map = new Map<string, { count: number; card: CardWithId }>();
  for (const card of deck) {
    const existing = map.get(card.id);
    if (existing) {
      existing.count++;
    } else {
      map.set(card.id, { count: 1, card });
    }
  }
  return map;
}

/**
 * 2つのデッキを比較
 */
export function compareDeck(npc1: SimNPC, npc2: SimNPC): DeckComparisonResult {
  const map1 = deckToCardCountMap(npc1.deck);
  const map2 = deckToCardCountMap(npc2.deck);

  const allCardIds = new Set([...map1.keys(), ...map2.keys()]);

  const commonCards: CardCountMap = {};
  const deck1OnlyCards: CardCountMap = {};
  const deck2OnlyCards: CardCountMap = {};

  let commonCount = 0;
  let totalUnique = allCardIds.size;

  for (const cardId of allCardIds) {
    const in1 = map1.get(cardId);
    const in2 = map2.get(cardId);

    if (in1 && in2) {
      // 共通カード
      const card = in1.card;
      commonCards[cardId] = {
        count: Math.min(in1.count, in2.count),
        name: card.name,
        attribute: (card as any).attribute || 'なし',
        cost: card.cost,
      };
      commonCount++;
    } else if (in1) {
      // デッキ1のみ
      deck1OnlyCards[cardId] = {
        count: in1.count,
        name: in1.card.name,
        attribute: (in1.card as any).attribute || 'なし',
        cost: in1.card.cost,
      };
    } else if (in2) {
      // デッキ2のみ
      deck2OnlyCards[cardId] = {
        count: in2.count,
        name: in2.card.name,
        attribute: (in2.card as any).attribute || 'なし',
        cost: in2.card.cost,
      };
    }
  }

  // Jaccard係数: 共通 / 全ユニーク
  const similarity = totalUnique > 0 ? Math.round((commonCount / totalUnique) * 100) : 0;

  // 属性構成
  const attributeComparison = {
    deck1: getAttributeDistribution(npc1.deck),
    deck2: getAttributeDistribution(npc2.deck),
  };

  // コスト分布
  const costComparison = {
    deck1: getCostDistribution(npc1.deck),
    deck2: getCostDistribution(npc2.deck),
  };

  return {
    similarity,
    commonCards,
    deck1OnlyCards,
    deck2OnlyCards,
    attributeComparison,
    costComparison,
  };
}

/**
 * 属性分布を取得
 */
function getAttributeDistribution(deck: CardWithId[]): { [attribute: string]: number } {
  const dist: { [attribute: string]: number } = {};
  for (const card of deck) {
    const attr = (card as any).attribute || 'なし';
    dist[attr] = (dist[attr] || 0) + 1;
  }
  return dist;
}

/**
 * コスト分布を取得
 */
function getCostDistribution(deck: CardWithId[]): { low: number; mid: number; high: number } {
  let low = 0, mid = 0, high = 0;
  for (const card of deck) {
    if (card.cost <= 2) low++;
    else if (card.cost <= 5) mid++;
    else high++;
  }
  return { low, mid, high };
}

/**
 * 世代別統計のオプション
 */
export interface GenerationStatsOptions {
  includeDeceased?: boolean; // 死亡NPCを含める（デフォルト: false）
  excludeImmigrants?: boolean; // 移住者を除外する（デフォルト: false）
}

/**
 * 移住者かどうかを判定
 */
function isImmigrant(npc: SimNPC): boolean {
  // 誕生日が0日目でなく、親がいない = 移住者
  return npc.birthDay > 0 && npc.family.filter(f => f.relationType === 'parent').length === 0;
}

/**
 * 世代別デッキ統計を計算
 */
export function calculateGenerationStats(
  npcs: SimNPC[],
  options: GenerationStatsOptions = {}
): GenerationDeckStats[] {
  const { includeDeceased = false, excludeImmigrants = false } = options;

  // 世代ごとにグループ化
  const byGeneration = new Map<number, SimNPC[]>();
  for (const npc of npcs) {
    // 死亡フィルタ
    if (!includeDeceased && !npc.isAlive) continue;
    // 移住者フィルタ
    if (excludeImmigrants && isImmigrant(npc)) continue;

    const gen = npc.generation || 1;
    const list = byGeneration.get(gen) || [];
    list.push(npc);
    byGeneration.set(gen, list);
  }

  const results: GenerationDeckStats[] = [];

  for (const [generation, genNpcs] of byGeneration) {
    if (genNpcs.length === 0) continue;

    // 平均レーティング・勝率
    const avgRating = genNpcs.reduce((sum, n) => sum + n.rating, 0) / genNpcs.length;
    const avgWinRate = genNpcs.reduce((sum, n) => {
      const total = n.wins + n.losses;
      return sum + (total > 0 ? n.wins / total : 0.5);
    }, 0) / genNpcs.length;

    // カード使用統計
    const cardUsage = new Map<string, { count: number; name: string; npcCount: number }>();
    for (const npc of genNpcs) {
      const seen = new Set<string>();
      for (const card of npc.deck) {
        const existing = cardUsage.get(card.id);
        if (existing) {
          existing.count++;
          if (!seen.has(card.id)) {
            existing.npcCount++;
            seen.add(card.id);
          }
        } else {
          cardUsage.set(card.id, { count: 1, name: card.name, npcCount: 1 });
          seen.add(card.id);
        }
      }
    }

    // トップカード（採用率順）
    const topCards = Array.from(cardUsage.entries())
      .map(([cardId, data]) => ({
        cardId,
        name: data.name,
        avgCount: data.count / genNpcs.length,
        usageRate: (data.npcCount / genNpcs.length) * 100,
      }))
      .sort((a, b) => b.usageRate - a.usageRate)
      .slice(0, 20);

    // 属性分布（全デッキ合計）
    const attributeDistribution: { [attr: string]: number } = {};
    for (const npc of genNpcs) {
      for (const card of npc.deck) {
        const attr = (card as any).attribute || 'なし';
        attributeDistribution[attr] = (attributeDistribution[attr] || 0) + 1;
      }
    }
    // 平均化
    const totalCards = genNpcs.length * 40;
    for (const attr of Object.keys(attributeDistribution)) {
      attributeDistribution[attr] = Math.round((attributeDistribution[attr] / totalCards) * 100);
    }

    results.push({
      generation,
      npcCount: genNpcs.length,
      avgRating: Math.round(avgRating),
      avgWinRate: Math.round(avgWinRate * 100),
      topCards,
      attributeDistribution,
    });
  }

  // 世代順にソート
  results.sort((a, b) => a.generation - b.generation);

  return results;
}

/**
 * 世代間のカード変化を分析
 */
export function analyzeGenerationChanges(
  stats: GenerationDeckStats[]
): Array<{
  fromGen: number;
  toGen: number;
  increased: Array<{ name: string; change: number }>;
  decreased: Array<{ name: string; change: number }>;
}> {
  const changes: Array<{
    fromGen: number;
    toGen: number;
    increased: Array<{ name: string; change: number }>;
    decreased: Array<{ name: string; change: number }>;
  }> = [];

  for (let i = 0; i < stats.length - 1; i++) {
    const from = stats[i];
    const to = stats[i + 1];

    const fromCards = new Map(from.topCards.map(c => [c.cardId, c]));
    const toCards = new Map(to.topCards.map(c => [c.cardId, c]));

    const allCards = new Set([...fromCards.keys(), ...toCards.keys()]);

    const increased: Array<{ name: string; change: number }> = [];
    const decreased: Array<{ name: string; change: number }> = [];

    for (const cardId of allCards) {
      const fromRate = fromCards.get(cardId)?.usageRate || 0;
      const toRate = toCards.get(cardId)?.usageRate || 0;
      const change = toRate - fromRate;
      const name = fromCards.get(cardId)?.name || toCards.get(cardId)?.name || cardId;

      if (change > 10) {
        increased.push({ name, change: Math.round(change) });
      } else if (change < -10) {
        decreased.push({ name, change: Math.round(change) });
      }
    }

    increased.sort((a, b) => b.change - a.change);
    decreased.sort((a, b) => a.change - b.change);

    changes.push({
      fromGen: from.generation,
      toGen: to.generation,
      increased: increased.slice(0, 5),
      decreased: decreased.slice(0, 5),
    });
  }

  return changes;
}
